import { ReactElement } from 'react'

import { CartGridRowProps } from '../../client/components/cart/src/cart-grid.js'
import { ErrorInSubscriptionDataSimple } from '../errors/subscription-errors.js'
import { translate } from '../i18n.js'
import { calculateGross, calculateNet } from '../utils/maths.js'
import { formatMonthlyPrice } from '../utils/utils.js'
import { CommonSubscriptionProduct } from './common-subscription-product.js'

export class CommonSubscriptionValueProduct extends CommonSubscriptionProduct {
    get debug() {
        super.debug
        console.log({
            class: 'SubscriptionValueProduct',
            'Monthly Price GROSS': this.baseSubscriptionCostGross,
            'VAT %': this.plan.countryVat,
            'Product VAT %': this.plan.countryProductVat,
            'Monthly Price NET': this.baseSubscriptionCostNet,
            'Discount Regular GROSS': this.discounts.general,
            'Discount Regular': this.regularDiscountNet,
            'Discount TI': this.discounts.ignoreVat,

            'Subscription Base Cost NET': this.baseSubscriptionCostNet,
            'Regular Discount NET': this.monthlyRegularDiscountNet,
            'Total NET': this.totalNet,
            'Total Gross': this.totalGross,
            'TradeIn Discount': this.monthlyVatIgnoringDiscount,

            'TOTAL PAYABLE': this.payablePriceGross,
        })

        return 1
    }

    get priceDescription(): string | ReactElement {
        const discount = this.payablePriceGrossNumber - this.baseSubscriptionCostGross

        if (Math.round(discount) < 0) {
            return translate('cart.includesDiscount', {
                lng: this.locale,
                discount: formatMonthlyPrice(discount, this.currencySymbol, this.locale),
            })
        }

        return ''
    }

    get cartRows() {
        const rows: CartGridRowProps[] = []
        const hasDiscount = this.payablePriceGrossNumber !== this.baseSubscriptionCostGross
        const monthlyPrice = this.payablePriceGrossNumber

        rows.push({
            left: {
                text: translate('cart.monthlyCost', {
                    lng: this.locale,
                }),
            },
            right: {
                isBlue: hasDiscount,
                text: this.payablePriceGross,
            },
        })

        if (hasDiscount) {
            rows.push({
                left: {
                    text: this.priceDescription,
                    weight: 'regular',
                },
            })
        }

        return {
            monthly: monthlyPrice,
            rows,
        }
    }

    get displayedPrice() {
        return this.payablePriceGross
    }

    private get regularDiscountNet() {
        if (this.plan.countryProductVat === undefined) {
            throw new ErrorInSubscriptionDataSimple('C2', 'Missing country VAT or Product Plan')
        }

        const netDiscount = calculateNet(this.discounts.general, this.plan.countryProductVat)

        return Math.max(0, netDiscount)
    }

    private get monthlyRegularDiscountNet() {
        return Math.max(0, this.regularDiscountNet / this.plan.duration)
    }

    get totalNet() {
        return this.baseSubscriptionCostNet - this.monthlyRegularDiscountNet
    }

    private get totalGross() {
        if (!this.plan.countryProductVat) {
            throw new ErrorInSubscriptionDataSimple('C3', 'Missing country VAT or Product Plan')
        }

        return calculateGross(this.totalNet, this.plan.countryProductVat)
    }

    private get monthlyVatIgnoringDiscount() {
        return Math.max(0, this.discounts.ignoreVat / this.plan.duration)
    }

    get payablePriceGrossNumber() {
        return Math.max(0, this.totalGross - this.monthlyVatIgnoringDiscount)
    }

    get payablePriceGross() {
        return formatMonthlyPrice(this.payablePriceGrossNumber, this.currencySymbol, this.locale)
    }

    get displayedPriceNumber() {
        return this.payablePriceGrossNumber
    }

    get planDuration() {
        return this.plan.duration
    }

    get discountDuration() {
        return this.plan.duration
    }
}
